// Npm
import { FieldErrors, FieldValues } from 'react-hook-form';

// Local
import legalEntityConstants from '../constants/legalEntity';
import {
  Address,
  AddressType,
  BeneficialOwnerFields,
  ClubbedEntityDetails,
  CorporateSignerDetails,
  EntityCategory,
  ExternalPartySource,
  FieldRules,
  FormElement,
  IBasicDetails,
  InputType,
  ISearchParams,
  IWorkItem,
  OfficerFields,
  Parties,
  PartyAddress,
  ReferenceData,
  RequiredFields,
  RulesKeys,
  RulesType,
  RulesValue,
  SignerRelatedParty
} from '../utils/types';
const moment = require('moment');

const parseRules = (rules: string) => {
  return JSON.parse(rules.replace(/'/g, '"'));
};

const getFormValue = (e: any, type: string) => {
  switch (type as InputType) {
    case InputType.TEXT:
    case InputType.RADIO:
      return e.target.value;
    case InputType.DROPDOWN:
    case InputType.DROPDOWN_MULTISELECT:
      return e.value;
    case InputType.DATE:
      return e;
  }
};

const recipientDetails: string[] = [
  'firstName',
  'middleName',
  'lastName',
  'addressLineOne',
  'addressLineTwo',
  'city',
  'state',
  'postalCode',
  'primaryEmailType',
  'primaryEmailAddress',
  'primaryPhoneNumberType',
  'primaryPhoneNumber'
];

const mergeObjectToString = (object: Address, separator: string, keyOrder: string[]): string => {
  const validEntries = Object.entries(object).filter(
    ([key, value]) => key !== 'id' && value !== '' && value && key !== 'isNew' && key !== 'type' && key !== 'addressType' && key !== 'addressPurpose' && key !== 'partyId'
  );
  validEntries.sort((a, b) => {
    const indexA = keyOrder.indexOf(a[0]);
    const indexB = keyOrder.indexOf(b[0]);
    return indexA - indexB;
  });
  const mergedAddress: string = validEntries
    .map(([key, value]) => {
      let displayValue = value;
      if (separator !== '' && key === separator) {
        displayValue = '\n' + value;
      }
      return displayValue;
    })
    .filter(data => data !== undefined)
    .join(', ')
    .trim()
    .toString();
  return mergedAddress;
};

const convertFileSizeToMB = (fileSize: number) => {
  return `${(fileSize / (1024 * 1024)).toFixed(2)} MB`;
};

const convertDate = (uploadedDate: string) => {
  return `${new Date(uploadedDate).toLocaleString()}, ${new Date(uploadedDate).toUTCString().split(' ')[5]}`;
};

const getFormattedTaxId = (taxId: any, taxIdNumber: string = '') => {
  let formattedInputValue = taxId;
  if (taxId) {
    if (!taxId.includes('-')) {
      const formattedValue = taxId.replace(/\D/g, '');
      if (formattedValue.length === 9) {
        formattedInputValue = `${formattedValue.slice(0, 2)}-${'X'.repeat(7)}`;
      }
    } else if (taxId.includes('-') && taxId.length > 10) {
      formattedInputValue = taxId.slice(0, 10);
    } else {
      formattedInputValue = taxIdNumber.slice(0, 8);
    }
  }
  return formattedInputValue;
};

const getRequiredFields = (rulesData: FieldRules[]): RequiredFields => {
  return rulesData?.reduce((acc: any, attribute: FieldRules) => {
    const updatedAcc = { ...acc, [attribute?.attributeName]: attribute?.mandatory };
    return updatedAcc;
  }, {});
};

const getEditableFields = (rulesData: FieldRules[], isEditMode: boolean = false): RequiredFields => {
  return rulesData?.reduce((acc: any, attribute: FieldRules) => {
    acc = { ...acc, [attribute?.attributeName]: isEditMode ? (attribute?.editable ?? false) : true };
    return acc;
  }, {});
};

const getFieldRule = (rulesData: FieldRules[], fieldName: string): FieldRules => {
  let fieldRule: FieldRules = {
    attributeName: '',
    attributePattern: '',
    editable: true,
    mandatory: false,
    min: 9999,
    max: 9999
  };
  if (rulesData) {
    const ruleObject = rulesData.find((rules: FieldRules) => rules.attributeName === fieldName);
    // AB#4939872 Temp fix to have 11 digits US phone number
    if (ruleObject) {
      fieldRule = { ...ruleObject };
      if (fieldName === 'primaryPhoneNumber' || fieldName === 'alternatePhoneNumber') {
        fieldRule.attributePattern = "^1[0-9]\\d{2}[0-9]\\d{6}$";
      }
    }
  }
  return fieldRule;
};

const mergeAddress = (obj: Address, separator: string, keyOrder: any) => {
  const validEntries = Object.entries(obj).filter(([key, value]) => (key !== 'id' && key !== 'addressType' && key !== 'addressPurpose' && key !== 'partyId') && value !== '');
  validEntries.sort((a, b) => {
    const indexA = keyOrder.indexOf(a[0]);
    const indexB = keyOrder.indexOf(b[0]);
    return indexA - indexB;
  });
  const mergeLineString = validEntries
    .map(([key, value]) => {
      if (key === 'line1' || key === 'line2' || key === 'line3') {
        return value;
      }
    })
    .join(' ')
    .trim();
  const mergeOtherString = validEntries
    .map(([key, value]) => {
      if (key !== 'line1' && key !== 'line2' && key !== 'line3') {
        return value;
      }
    })
    .filter(data => data !== undefined)
    .join(separator)
    .trim();
  const mergeString = mergeLineString + ', ' + mergeOtherString;
  return mergeString;
};

const debounce = (func: any, timeout = 500) => {
  let timer: any;
  return (...args: any) => {
    args[0]?.persist();
    clearTimeout(timer);
    timer = setTimeout(() => {
      func.call(this, args[0]);
    }, timeout);
  };
};

const formatDate = (date: string | undefined) => {
  let formattedDate = '';
  if (date) {
    formattedDate = moment(date).format('YYYY-MM-DD');
  }
  return formattedDate;
};

const getFormattedInputValue = (value: string, taxIdentificationNumber: string, taxType?: string): string => {
  let returnValue: string = '';
  if (!value?.includes('-')) {
    const formattedValue = value.length > 9 ? value.slice(0, 9) : value;
    if (formattedValue.length === 9 && taxType === '0007') {
      returnValue = formattedValue.replace(/(\d{2}\d{7})/, 'XX-XXXXXXX');
    } else {
      returnValue = formattedValue.replace(/(\d{3}\d{2}\d{4})/, 'XXX-XX-XXXX');
    }
  } else {
    const maskValueLength = taxType === '0007' ? 10 : 11;
    if (value.length > maskValueLength) {
      returnValue = value?.slice(0, maskValueLength);
    } else {
      returnValue = taxIdentificationNumber?.slice(0, 8);
    }
  }
  return returnValue;
};

const getMaskedNumber = (value: string, taxIdentificationNumber: string, taxType?: string): string => {
  let formattedInputValue = value ?? '';
  if (value && value !== '') {
    formattedInputValue = getFormattedInputValue(value, taxIdentificationNumber, taxType);
  }
  return formattedInputValue;
};

const signerTaxIdDuplicateCheck = (
  officerId: string,
  officerList: OfficerFields[],
  beneficialOwnersList: BeneficialOwnerFields[],
  signerDetails: CorporateSignerDetails[] = [],
  signerId: string = ''
) => {
  const corporateTaxIdList = officerList
    .filter((a: OfficerFields) => a.officerType === 'Yes' && a.officerId !== officerId)
    .map((a: OfficerFields) => a.taxIdentificationNumber)
    .filter((data: string | undefined) => data !== '');

  const individualTaxIdList = officerList
    .filter((a: OfficerFields) => a.officerType === 'No' && a.officerId !== officerId)
    .map((a: OfficerFields) => a.taxIdentificationNumber)
    .filter((data: string | undefined) => data !== '');

  const beneficialTaxIdList = beneficialOwnersList
    .filter(b => b.officerId !== officerId)
    .map(a => a.taxIdentificationNumber)
    .filter((data: string | undefined) => data !== '');

  const corporateSignerTaxIdList = officerList
    .filter((a: OfficerFields) => a.officerType === 'Yes' && a.officerId !== officerId)
    .flatMap((b: OfficerFields) => b.signerDetails)
    .map((a: CorporateSignerDetails | undefined) => a?.taxIdentificationNumber)
    .filter((data: string | undefined) => data !== '');

  const currentSignerTaxIdList = signerDetails
    .filter((a: CorporateSignerDetails) => a.id !== signerId)
    .map((a: CorporateSignerDetails) => a.taxIdentificationNumber)
    .filter((data: string | undefined) => data !== '');

  return [
    ...corporateTaxIdList,
    ...beneficialTaxIdList,
    ...corporateSignerTaxIdList,
    ...individualTaxIdList,
    ...currentSignerTaxIdList
  ].filter((data: string | undefined) => data !== '');
};

const getLegalEntityCategory = (legalEntityCategory: string): string => {
  switch (legalEntityCategory as EntityCategory) {
    case EntityCategory.CORPORATION:
      return EntityCategory.CORPORATION_LE;
    case EntityCategory.INDIVIDUAL:
      return EntityCategory.INDIVIDUAL_LE;
    case EntityCategory.TRUST:
      return EntityCategory.TRUST_LE;
    case EntityCategory.CORPORATION_LE:
      return EntityCategory.CORPORATION;
    case EntityCategory.INDIVIDUAL_LE:
      return EntityCategory.INDIVIDUAL;
    case EntityCategory.TRUST_LE:
      return EntityCategory.TRUST;
    default:
      return '';
  }
};

const objectToQueryString = (params: ISearchParams) => {
  return Object.entries(params)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');
};

const getElement = (...formElement: any) => {
  return {
    label: formElement[0],
    name: formElement[1],
    placeholder: formElement[2],
    required: formElement[3],
    type: formElement[4],
    error: formElement[5],
    disabled: formElement[6],
    icon: formElement[7],
    options: formElement[8],
    dateFormat: formElement[9],
    className: formElement[10],
    id: formElement[11]
  } as FormElement;
};

const getFirstNameFormElement = (requiredFields: RequiredFields, errors: FieldErrors<FieldValues>) =>
  getElement(
    'First Name',
    'firstName',
    'Enter First Name',
    requiredFields?.firstName,
    InputType.TEXT,
    errors?.firstName?.message as string
  );

const getMiddleNameFormElement = (requiredFields: RequiredFields, errors: FieldErrors<FieldValues>) =>
  getElement(
    'Middle Name',
    'middleName',
    'Enter Middle Name',
    requiredFields?.middleName,
    InputType.TEXT,
    errors?.middleName?.message as string
  );

const getLastNameFormElement = (requiredFields: RequiredFields, errors: FieldErrors<FieldValues>) =>
  getElement(
    'Last Name',
    'lastName',
    'Enter Last Name',
    requiredFields?.lastName,
    InputType.TEXT,
    errors?.lastName?.message as string
  );

const getSuffixFormElement = (
  requiredFields: RequiredFields,
  errors: FieldErrors<FieldValues>,
  options: ReferenceData[] | undefined,
  editableFields?: RequiredFields
) =>
  getElement(
    'Suffix',
    'suffix',
    'Select...',
    requiredFields?.suffix,
    InputType.DROPDOWN,
    errors?.suffix?.message as string,
    editableFields && !editableFields?.suffix || false,
    '',
    options
  );

const getTaxIdentificationNumTypeFormElement = (
  requiredFields: RequiredFields,
  errors: FieldErrors<FieldValues>,
  options: ReferenceData[] | undefined,
  className?: string,
  editableFields?: RequiredFields
) =>
  getElement(
    'Tax Identification Number Type',
    'taxIdentificationNumType',
    'Select...',
    requiredFields?.taxIdentificationNumType,
    InputType.DROPDOWN,
    errors?.taxIdentificationNumType?.message as string,
    editableFields && !editableFields?.taxIdentificationNumType || false,
    '',
    options,
    '',
    className
  );

const getTaxIdentificationNumberFormElement = (
  requiredFields: RequiredFields,
  errors: FieldErrors<FieldValues>,
  isMasked: boolean,
  className?: string,
  editableFields?: RequiredFields
) => {
  return {
    ...getElement(
      'Tax Identification Number',
      'taxIdentificationNum',
      'Enter Tax Identification Number',
      requiredFields?.taxIdentificationNum,
      InputType.TEXT,
      errors?.taxIdentificationNum?.message as string,
      false,
      isMasked ? 'visibility_off_outline' : 'visibility_outline',
      '',
      '',
      className
    ),
    readOnly: editableFields && !editableFields?.taxIdentificationNum || false
  };
};

const getGovIssuedIdentificationNumberFormElement = (
  requiredFields: RequiredFields,
  errors: FieldErrors<FieldValues>,
  isGovIdMasked: boolean,
  editableFields?: RequiredFields
) => {
  return {
    ...getElement(
      'Government-Issued Identification Number',
      'docIdentificationNum',
      'Enter Government-Issued Identification Number',
      requiredFields?.docIdentificationNum,
      InputType.TEXT,
      errors?.docIdentificationNum?.message as string,
      false,
      isGovIdMasked ? 'visibility_off_outline' : 'visibility_outline'
    ),
    readOnly: editableFields && !editableFields?.docIdentificationNum || false
  };
};

const getCountryOfIssuanceFormElement = (
  requiredFields: RequiredFields,
  errors: FieldErrors<FieldValues>,
  options: ReferenceData[] | undefined,
  editableFields?: RequiredFields,
) =>
  getElement(
    'Country of Issuance',
    'docIssueCountryCd',
    'Select...',
    requiredFields?.docIssueCountryCd,
    InputType.DROPDOWN,
    errors?.docIssueCountryCd?.message as string,
    editableFields && !editableFields?.docIssueCountryCd || false,
    '',
    options
  );

const getStateOfIssuanceFormElement = (
  requiredFields: RequiredFields,
  errors: FieldErrors<FieldValues>,
  options: ReferenceData[] | undefined,
  editableFields?: RequiredFields,
) =>
  getElement(
    'State of Issuance',
    'docIssueStateProvinceCd',
    'Select...',
    requiredFields?.docIssueStateProvinceCd,
    InputType.DROPDOWN,
    errors?.docIssueStateProvinceCd?.message as string,
    editableFields && !editableFields?.docIssueStateProvinceCd || false,
    '',
    options
  );

const getUserDetails = (address: Address, countryList: ReferenceData[], statesList: ReferenceData[]) => {
  const addressObject = { ...address };
  addressObject.country = countryList.find((item: ReferenceData) => item.value === addressObject.country)?.label;
  addressObject.state = statesList.find((item: ReferenceData) => item.value === addressObject.state)?.label;
  return mergeObjectToString(addressObject, 'state', legalEntityConstants.addressFields);
};

const getTotalOwnershipPercentage = (
  beneficialOwnersList: BeneficialOwnerFields[],
  officerId: string | number = ''
): number => {
  return beneficialOwnersList
    .filter(a => a.officerId !== officerId)
    .map(data => Number(data.percentageOfOwnership))
    .reduce((total, percentage) => total + percentage, 0);
};

const checkPartyIsExternal = (externalPartySource: string, externalPartyUlek: string): boolean => {
  if (
    externalPartySource &&
    externalPartyUlek &&
    (externalPartySource as ExternalPartySource) === ExternalPartySource.EXTERNAL &&
    externalPartyUlek !== ''
  ) {
    return true;
  } else {
    return false;
  }
};

const getEntityName = (legalEntityCategory: string, entityDetails: ClubbedEntityDetails) => {
  let entityName;
  if ((legalEntityCategory as EntityCategory) === EntityCategory.INDIVIDUAL) {
    entityName = `${entityDetails?.firstName}${entityDetails?.middleName ? ' ' + entityDetails?.middleName : ''} ${entityDetails?.lastName
    }`;
  } else {
    entityName = entityDetails?.fulLegalEntityName;
  }
  return entityName;
};

const getPartyName = (ele: IWorkItem): string => {
  const {
    entityDetails: { basicDetails }
  } = ele;
  const { type, name, firstName, middleName, lastName } = basicDetails as IBasicDetails;

  let fullName: string = '';
  if (type === EntityCategory.CORPORATION || type === EntityCategory.TRUST) {
    fullName = name;
  } else {
    fullName = `${firstName}${middleName ? ' ' + middleName : ''} ${lastName}`;
  }
  return fullName;
};

const getCorporateAndIndividualParties = (relatedParties: OfficerFields[]): SignerRelatedParty[] => {
  let allParties: SignerRelatedParty[] = [];
  allParties = relatedParties?.reduce((acc: SignerRelatedParty[], officer: OfficerFields) => {
    if (officer.officerType === 'No') {
      acc.push({
        taxIdentificationNum: officer.taxIdentificationNumber,
        externalPartySource: officer.externalPartySource,
        externalPartyId: officer.externalPartyId,
        externalPartyUlek: officer.externalPartyUlek
      } as SignerRelatedParty);
    } else {
      const { signerDetails } = officer;
      signerDetails?.forEach((signer: CorporateSignerDetails) => {
        acc.push({
          taxIdentificationNum: signer.taxIdentificationNumber,
          externalPartySource: signer.externalPartySource,
          externalPartyId: signer.externalPartyId,
          externalPartyUlek: signer.externalPartyUlek
        } as SignerRelatedParty);
      });
    }
    return acc;
  }, []);
  return allParties;
};

const getAllParties = (relatedParties: Parties, legalEntityType: string): SignerRelatedParty[] => {
  const { officers, coOwners, fiduciary } = relatedParties;
  let allParties: SignerRelatedParty[] = [];

  switch (legalEntityType as EntityCategory) {
    case EntityCategory.CORPORATION:
      allParties = getCorporateAndIndividualParties(officers);
      break;

    case EntityCategory.INDIVIDUAL:
      allParties = coOwners?.map((owner: BeneficialOwnerFields) => {
        return {
          taxIdentificationNum: owner.taxIdentificationNumber,
          externalPartySource: owner.externalPartySource,
          externalPartyId: owner.externalPartyId,
          externalPartyUlek: owner.externalPartyUlek
        } as SignerRelatedParty;
      });
      break;
    case EntityCategory.TRUST:
      allParties = getCorporateAndIndividualParties(fiduciary);
      break;
  }
  return allParties;
};

/**
Transform a given label string into a formatted string where
the first word is in lowercase and subsequent words are capitalized.

@param {string} label - The input label string to be transformed.
@returns {string} - The formatted label string.
@throws {TypeError} - Throws an error if the input is not a string.
**/
const getLabelName = (label: string): string => {
  if (typeof label !== 'string') {
    throw new TypeError('Input must be a string');
  }
  return label
    .trim()
    .split(/\s+/)
    .map((word, index) =>
      index === 0 ? word.toLowerCase() : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    )
    .join('');
};

const getTotalOfficer = (officerList: OfficerFields[], legalEntityCategory: EntityCategory): number => {
  let totalCount: number = 0;
  if ((legalEntityCategory) === EntityCategory.CORPORATION || legalEntityCategory === EntityCategory.TRUST) {
    const signersArr = officerList
      .filter((a: OfficerFields) => a.officerType === 'Yes')
      .flatMap((b: OfficerFields) => b.signerDetails);
    const individualArr = officerList.filter((a: OfficerFields) => a.officerType === 'No');
    totalCount = signersArr.length + individualArr.length;
  }
  return totalCount;
};

const getCownershipPayload = (entityDetails: ClubbedEntityDetails) => {
  return {
    input: {
      id: RulesType.LEGALENTITY_MANDATORY_RULES,
      queryParam: [
        {
          key: RulesKeys.DOMAIN,
          value: RulesType.LEGAL_ENTITY
        },
        {
          key: RulesKeys.TYPE,
          value: RulesValue.INDIVIDUAL
        },
        {
          key: RulesKeys.SUBTYPE,
          value: 'CO-OWNER-DROPDOWN'
        }
      ],
      dataSet: {
        partyType: RulesType.INDIVIDUAL_LE,
        maritalStatus: entityDetails?.maritalStatus,
        address: [
          {
            state: entityDetails?.address?.filter((address: PartyAddress) => address.type === AddressType.LEGAL)[0]
              ?.state,
            type: AddressType.LEGAL
          }
        ]
      }
    }
  };
};

const getTimeDifference = (timestamp1: string, timestamp2: string): number =>
  moment.duration(moment(timestamp2).diff(moment(timestamp1))).asSeconds();

const getAlternatePhoneNumberType = (telephoneNumberTypeList: ReferenceData[]): ReferenceData[] => {
  return telephoneNumberTypeList.filter((item: ReferenceData) => item.value !== '1');
};

const generateRequiredDocuments = (
  legalEntityCategory: string,
  currentRoute: string,
  inCorporationDate: string,
  relatedParty: any[],
  coOwners: any[],
  usTaxReturnInd: string,
  prfTaxExmptGrntdIndVal: string,
  isTaxExemptFeatureEnabled: boolean = false
) => {
  const getEntityDataSet = () => {
    let entityDataSet = {};
    if (legalEntityCategory === 'Individual') {
      entityDataSet = {
        partyType: RulesType.INDIVIDUAL_LE,
        relatedParties:
          currentRoute === 'entitydetails' || !coOwners.length ? [] : [{ partyType: 'INDIVIDUAL_CO_OWNER' }]
      };
    } else if (legalEntityCategory === 'Corporation') {
      entityDataSet = {
        partyType: RulesType.CORPORATION_LE,
        orgIncorporationDt: inCorporationDate,
        relatedParties: currentRoute === 'entitydetails' ? [] : relatedParty,
        prfTaxExmptGrntdInd: prfTaxExmptGrntdIndVal?.toLowerCase(),
        usTaxReturnInd: usTaxReturnInd?.toLowerCase()
      };
    } else {
      entityDataSet = {
        partyType: RulesType.TRUST_LE,
        relatedParties: currentRoute === 'entitydetails' ? [] : [{ partyType: 'CORPORATE_FIDUCIARY' }],
        ...(isTaxExemptFeatureEnabled ? {
          prfTaxExmptGrntdInd: prfTaxExmptGrntdIndVal?.toLowerCase(),
          usTaxReturnInd: usTaxReturnInd?.toLowerCase()
        } : {})
      };
    }
    return entityDataSet;
  };

  const getRulesKeysValue = () => {
    switch (legalEntityCategory) {
      case 'Corporation':
        return RulesValue.CORPORATION;
      case 'Individual':
        return RulesValue.INDIVIDUAL;
      case 'Trust':
        return RulesValue.TRUST;
      default:
        return RulesValue.CORPORATION;
    }
  };

  return {
    input: {
      id: RulesType.REQUIRED_DOCS,
      queryParam: [
        {
          key: RulesKeys.DOMAIN,
          value: RulesValue.LEGAL_ENTITY
        },
        {
          key: RulesKeys.TYPE,
          value: getRulesKeysValue()
        }
      ],
      dataSet: getEntityDataSet()
    }
  };
};

const powerHoldingRolesKeys = ['grantorTrust', 'initialTrustee', 'successorTrustee', 'trustAmended', 'trustRevocable'];

const getLegalEntityName = (officer: OfficerFields): string => {
  let name: string = '';
  if (officer?.officerType === 'Yes') {
    name = `${officer?.fulLegalEntityName} (${officer?.signerDetails?.length})`;
  } else {
    name = `${officer?.firstName}${officer?.middleName ? ' ' + officer?.middleName : ''} ${officer?.lastName}`;
  }
  return name;
};

const compareEntityDetails = (obj1: Record<string, any>, obj2: Record<string, any>): boolean => {
  if (typeof obj1 !== 'object' || obj1 === null) {
    return obj1 === obj2;
  }
  if (Array.isArray(obj1)) {
    if (!Array.isArray(obj2) || obj1.length !== obj2.length) return false;
    return obj1.every((item, idx) => compareEntityDetails(item, obj2[idx]));
  }
  return Object.keys(obj1).every(key => compareEntityDetails(obj1[key], obj2?.[key]));
};

const normalizeHref = (href: string): string => {
  return Array.from(href)
    .filter(ch => {
      const codePoint = ch.codePointAt(0);
      if (codePoint === undefined) return false;
      return !(codePoint <= 0x1f || codePoint === 0x7f || ch === ' ');
    })
    .join('');
};

const sanitizeExternalHref = (rawHref: unknown): string | undefined => {
  if (typeof rawHref !== 'string') return undefined;
  const href = rawHref.trim();
  if (!href) return undefined;
  const normalized = normalizeHref(href).toLowerCase();
  const blockedSchemeRegex = /^(?:javascript|data|vbscript):/i;
  if (blockedSchemeRegex.test(normalized)) {
    return undefined;
  }

  try {
    const url = new URL(href, window.location.origin);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return undefined;
    return url.href;
  } catch {
    return undefined;
  }
};

const getFirstMailingAddress = (mailingAddress: Address | Address[] | undefined): Address | {} => {
  if (Array.isArray(mailingAddress)) {
    return mailingAddress[0] || {};
  } else {
    return mailingAddress || {};
  }
};

/**
 * Merges multiple arrays of FieldRules into a single array by prioritizing rules passed last as priority 1
 * @param rulesArray
 * @returns
 */
const mergeRulesByPriority = (...rulesArray: FieldRules[][]): FieldRules[] => {
  return Object.values(
    rulesArray.flat().reduce<FieldRules>((acc, rule) => ({ ...acc, [rule.attributeName]: rule }),
      <FieldRules>{})
  );
};

export {
  convertDate,
  convertFileSizeToMB,
  getFormValue,
  parseRules,
  mergeObjectToString,
  getFormattedTaxId,
  getRequiredFields,
  getFieldRule,
  mergeAddress,
  debounce,
  formatDate,
  getMaskedNumber,
  signerTaxIdDuplicateCheck,
  getLegalEntityCategory,
  objectToQueryString,
  getElement,
  getFirstNameFormElement,
  getMiddleNameFormElement,
  getLastNameFormElement,
  getSuffixFormElement,
  getTaxIdentificationNumTypeFormElement,
  getTaxIdentificationNumberFormElement,
  getGovIssuedIdentificationNumberFormElement,
  getCountryOfIssuanceFormElement,
  getStateOfIssuanceFormElement,
  getUserDetails,
  getTotalOwnershipPercentage,
  checkPartyIsExternal,
  getEntityName,
  getPartyName,
  getAllParties,
  getLabelName,
  getTotalOfficer,
  getCownershipPayload,
  getTimeDifference,
  getAlternatePhoneNumberType,
  generateRequiredDocuments,
  recipientDetails,
  powerHoldingRolesKeys,
  getLegalEntityName,
  getEditableFields,
  compareEntityDetails,
  getFirstMailingAddress,
  mergeRulesByPriority,
  sanitizeExternalHref
};
